import {
  Container,
  styled,
  Box,
} from "@mui/material"
import { useContext, useEffect, useMemo } from 'react';
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormTextField from "components/form/FormTextField";
import FormSelect from "components/form/FormSelect";
import StyledButton from "components/button/StyledButton";
import { useNavigate } from "react-router-dom";
import { ProjectContext } from "context/ProjectContext";
import "../../components/button/StyledButton.css"
import { useGetFoundationTypesQuery, useGetHorizontalGravitySystemsQuery, useGetLateralSystemsQuery, useGetPodiumQuery, useGetRiskCategoriesQuery, useGetSeismicDesignCategoriesQuery, useGetSoilBearingPressureQuery, useGetUserQuery, useGetVerticalGravitySystemsQuery, useUpdateProjectMutation } from "api/api";
import { useCreateUpdateProject } from "hooks/useCreateUpdateProject";
import DynamicFormSelect from "components/form/DynamicFormSelect";
import SaveErrorAlert from "components/form/SaveErrorAlert";
import RevertToDraftDialog from "components/form/RevertToDraftDialog";
import DiscardChangesDialog from "components/form/DiscardChangesDialog";
import { useStepBackNavigation } from "hooks/useStepBackNavigation";
import { STEP_FIELDS } from "lib/utils/projectPatch";

// Shared by the schema and the field's inline hint so both say the same thing.
export const soilBearingOptionsMessage = (allowedSoilBearingPressures: readonly (string | number)[]) => {
  const ascending = [...allowedSoilBearingPressures].map(Number).sort((a, b) => a - b);
  const message = `Must be one of the listed values (${ascending.join(', ')})`;

  return message;
};

// Built per-render because the allowed soil bearing pressures come from the API.
// The backend resolves this field against the AllowableSoilBearingPressurePSF lookup
// table, so anything outside that list is rejected with a 400 - validate it here
// instead, where we can point at the field (ID 9).
export const createSchema = (allowedSoilBearingPressures: readonly (string | number)[]) => {
  const schema = z.object({
    tcg_long_direction_ft:
      z.union([
        z.string({ invalid_type_error: "Typical Column Grid is required" }).min(1, { message: "Typical Column Grid is required" })
          .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
          .refine((val) => Number(val) >= 0.0001, { message: "Must be above 0" }),
        z.number().min(0.0001, { message: "Must be above 0" })
      ]),

    risk_category:
      z.enum(["I", "II", "III", "IV"], {
        required_error: "Risk Category is required",
        invalid_type_error: "Risk Category is required"
      }),

    typical_floor_live_load_psf:
      z.union([
        z.string({ invalid_type_error: "Typical Floor Live Load is required" }).min(1, { message: "Typical Floor Live Load is required" })
          .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
          .refine((val) => Number(val) >= 0.0001, { message: "Must be above 0" }),
        z.number().min(0.0001, { message: "Must be above 0" })
      ]),

    ground_snow_load_psf:
      z.union([
        z.string({ invalid_type_error: "Ground Snow Load is required" }).min(1, { message: "Ground Snow Load is required" })
          .refine((val) => (!isNaN(Number(val)) || val !== null || val !== undefined), { message: "Must be a number" })
          .refine((val) => Number(val) >= 0, { message: "Must be a non-negative number" }),
        z.number().min(0, { message: "Must be a non-negative number" })
      ]),

    ultimate_wind_speed_mph:
      z.union([
        z.string({ invalid_type_error: "Ultimate Wind Speed is required" }).min(1, { message: "Ultimate Wind Speed is required" })
          .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
          .refine((val) => Number(val) >= 0, { message: "Must be a non-negative number" }),
        z.number().min(0, { message: "Must be a non-negative number" })
      ]),

    seismic_design_category:
      z.enum(['A', 'B', 'C', 'D', 'E', 'F'], { invalid_type_error: "Seismic Design Category is required" }),

    primary_horizontal_gravity_system:
      z.enum(['Concrete: PT Framing', 'Concrete: Non-PT Framing', 'Concrete: Precast', 'Concrete: Other', 'Steel: Frame + Concrete on Metal Deck', 'Steel: Frame + Bare Metal Deck', 'Steel: Other', 'Wood: Joists and Sheathing', 'Wood: Engineered Panels', 'Wood: Other', 'Other Material (not concrete, steel, or wood)'], { invalid_type_error: "Primary Horizontal Gravity System is required" }),

    primary_vertical_gravity_system:
      z.enum(['Concrete: CIP', 'Concrete: Precast', 'Concrete: Other', 'Steel: Columns', 'Steel: Cold-Formed', 'Steel: Other', 'Wood: Mass Timber', 'Wood: Light-Frame', 'Wood: Other', 'Masonry', 'Other Material (not concrete, steel, wood, or masonry)'], { invalid_type_error: "Primary Vertical Gravity System is required" }),

    primary_lateral_system:
      z.enum(['Concrete: Shear Walls', 'Concrete: Moment Frames', 'Concrete: Other', 'Steel: Braced Frames', 'Steel: Moment Frames', 'Steel: Other', 'Light Frame Shear Panels', 'Masonry: Shear Walls', 'Wood: Shear Panels', 'Wood: Other', 'Other'], { invalid_type_error: "Primary Lateral System is required" }),

    podium:
      z.enum(['Not a podium building', 'Primary system defined above is on a podium', 'Primary system defined above is a podium'], { invalid_type_error: "Podium is required" }),

    allowable_soil_bearing_pressure_psf:
      z.union([
        z.string({ invalid_type_error: "Allowable Soil Bearing Pressure is required" }).min(1, { message: "Allowable Soil Bearing Pressure is required" })
          .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
          .refine((val) => Number(val) >= 0, { message: "Must be a non-negative number" }),
        z.number().min(0, { message: "Must be a non-negative number" })
      ])
        .refine(
          // Before the lookup list loads there is nothing to check against, so let it
          // through rather than flagging a valid value.
          (val) => allowedSoilBearingPressures.length === 0
            || allowedSoilBearingPressures.some((allowed) => Number(allowed) === Number(val)),
          { message: soilBearingOptionsMessage(allowedSoilBearingPressures) }
        )
        .nullable(),

    foundation_type:
      z.enum(['Shallow Foundation', "Deep Foundation < 50'/15m", "Deep Foundation > 50'/15m", 'Other Foundation System'], { invalid_type_error: "Foundation Type is required" }),

  });

  return schema;
};

const AddProjectStructuralSystem = () => {
  const navigate = useNavigate();
  const { data: riskCategories = [] } = useGetRiskCategoriesQuery();
  const { data: seismicDesignCategories = [] } = useGetSeismicDesignCategoriesQuery();
  const { data: horizontalGravitySystems = [] } = useGetHorizontalGravitySystemsQuery();
  const { data: verticalGravitySystems = [] } = useGetVerticalGravitySystemsQuery();
  const { data: lateralSystems = [] } = useGetLateralSystemsQuery();
  const { data: podiums = [] } = useGetPodiumQuery();
  const { data: foundationTypes = [] } = useGetFoundationTypesQuery();
  const { data: soilBearingPressure = [] } = useGetSoilBearingPressureQuery();
  const { data: userData } = useGetUserQuery();

  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('ProjectContext error');
  }
  const { projectDetails, projectId } = context;

  const listSoilBearingPressure = useMemo(() => {
    const values = soilBearingPressure.map(item => item.value);

    return values;
  }, [soilBearingPressure]);

  const schema = useMemo(() => {
    const nextSchema = createSchema(listSoilBearingPressure);

    return nextSchema;
  }, [listSoilBearingPressure]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: projectDetails,
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  const { handleCreateUpdateProject, error: saveError, isLoading, revertPrompt } = useCreateUpdateProject({ userData })


  const listRiskCategories = riskCategories.map(item => item.name);
  const listSeismicDesignCategories = seismicDesignCategories.map(item => item.name);
  const listHorizontalGravitySystems = horizontalGravitySystems.map(item => item.name);
  const listVerticalGravitySystems = verticalGravitySystems.map(item => item.name);
  const listLateralSystems = lateralSystems.map(item => item.name);
  const listPodiums = podiums.map(item => item.name);
  const listFoundationTypes = foundationTypes.map(item => item.name);

  const inputLabels = [
    { name: "tcg_long_direction_ft", label: "Typical Column Grid, Long Direction (ft)", value: projectDetails['tcg_long_direction_ft'], component: "FormTextField" },
    { name: "risk_category", label: "Risk Category", value: projectDetails['risk_category'], component: "FormSelect", menuItems: listRiskCategories ?? ['test1', 'test2', 'test3'] },
    { name: "typical_floor_live_load_psf", label: "Typical Floor Live Load (psf)", value: projectDetails['typical_floor_live_load_psf'], component: "FormTextField" },
    { name: "ground_snow_load_psf", label: "Ground Snow Load (psf)", value: projectDetails['ground_snow_load_psf'], component: "FormTextField" },
    { name: "ultimate_wind_speed_mph", label: "Ultimate Wind Speed (mph)", value: projectDetails['ultimate_wind_speed_mph'], component: "FormTextField" },
    { name: "seismic_design_category", label: "Seismic Design Category", value: projectDetails['seismic_design_category'], component: "FormSelect", menuItems: listSeismicDesignCategories ?? ['test1', 'test2', 'test3'] },
    { name: "primary_horizontal_gravity_system", label: "Primary Horizontal Gravity System", value: projectDetails['primary_horizontal_gravity_system'], component: "FormSelect", menuItems: listHorizontalGravitySystems ?? ['test1', 'test2', 'test3'] },
    { name: "primary_vertical_gravity_system", label: "Primary Vertical Gravity System", value: projectDetails['primary_vertical_gravity_system'], component: "FormSelect", menuItems: listVerticalGravitySystems ?? ['test1', 'test2', 'test3'] },
    { name: "primary_lateral_system", label: "Primary Lateral System", value: projectDetails['primary_lateral_system'], component: "FormSelect", menuItems: listLateralSystems ?? ['test1', 'test2', 'test3'] },
    { name: "podium", label: "Podium", value: projectDetails['podium'], component: "FormSelect", menuItems: listPodiums ?? ['test1', 'test2', 'test3'] },
    { name: "foundation_type", label: "Foundation Type", value: projectDetails['foundation_type'], component: "FormSelect", menuItems: listFoundationTypes ?? ['test1', 'test2', 'test3'] },
    {
      name: "allowable_soil_bearing_pressure_psf",
      label: "Allowable Soil Bearing Pressure (psf)",
      value: projectDetails['allowable_soil_bearing_pressure_psf'],
      component: "DynamicFormSelect",
      menuItems: listSoilBearingPressure ?? ['test1', 'test2', 'test3']
    },
  ] as const;

  // Handle the case when Deep Foundation is selected which disables
  // the allowable_soil_bearing_pressure_psf input and sets its value to null
  const updateFormValues = (values: Record<string, any>) => {
    if (
      values.foundation_type === "Deep Foundation < 50'/15m" ||
      values.foundation_type === "Deep Foundation > 50'/15m"
    ) {
      return { ...values, allowable_soil_bearing_pressure_psf: null };
    }
    return values;
  };

  const buildFormValues = () => updateFormValues(getValues());

  // Wait for the save and stay on the step if it failed - otherwise the user advances
  // believing the data was stored (ID 7).
  const handleGoToNextForm = async () => {
    const updatedValues = buildFormValues();
    const saved = await handleCreateUpdateProject({
      formValues: updatedValues,
      leaveForm: false,
      fields: STEP_FIELDS[2],
    });

    if (!saved) return;

    navigate('/add-project/3');
  }

  const handleSaveAndExit = async () => {
    const updatedValues = buildFormValues();
    handleCreateUpdateProject({ formValues: updatedValues, fields: STEP_FIELDS[2] })
  }

  const { handleClickBack, showDiscardPrompt, cancelDiscard, confirmDiscard } = useStepBackNavigation({
    previousPath: '/add-project/1',
    fields: STEP_FIELDS[2],
    buildFormValues,
    isDirty,
    trigger,
    save: handleCreateUpdateProject,
  });

  useEffect(() => {
    reset(projectDetails);
  }, [projectDetails, projectId, reset]);

  return (
    <StyledContainer>
      <div style={{ width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 41, paddingTop: 37 }}>
        <Box sx={{ width: "365px" }}>
          <div style={{ width: '100%', height: '100%', color: '#1A1A1A', fontSize: 28, fontFamily: 'Helvetica Neue LT Std', fontWeight: '300', wordWrap: 'break-word', paddingBottom: 12 }}>Structural System</div>
          <form onSubmit={handleSubmit(handleGoToNextForm)}>
            {inputLabels.map((inputLabel) => {
              // variable to track the foundation type value, used to conditionally disable allowable_soil_bearing_pressure_psf
              const foundationTypeValue = watch("foundation_type");
              return (
              <Controller
                key={inputLabel.name}
                name={inputLabel.name}
                control={control}
                defaultValue={inputLabel.value}
                render={({ field }) => {
                  const errorMessage = errors[inputLabel.name]?.message as string | undefined
                  return (
                    <Box>
                      {
                        inputLabel.component === "DynamicFormSelect" ? (
                          <DynamicFormSelect
                            {...field}
                            label={inputLabel.label}
                            error={!!errors[inputLabel.name]}
                            helperText={errors[inputLabel.name]?.message}
                            menuItems={inputLabel.menuItems}
                            disabled={isSubmitting || (inputLabel.name === "allowable_soil_bearing_pressure_psf" && (foundationTypeValue === "Deep Foundation < 50'/15m" || foundationTypeValue === "Deep Foundation > 50'/15m"))}
                            value={field.value as any}
                            noMatchHelperText={soilBearingOptionsMessage(listSoilBearingPressure)}
                          />
                        ) : inputLabel.component === "FormSelect" ? (
                          <FormSelect
                            {...field}
                            label={inputLabel.label}
                            error={!!errors[inputLabel.name]}
                            helperText={errors[inputLabel.name]?.message}
                            menuItems={inputLabel.menuItems}
                            disabled={isSubmitting}
                          />
                        ) : (
                          <FormTextField
                            {...field}
                            value={field.value ?? ""}
                            label={inputLabel.label}
                            error={!!errors[inputLabel.name]}
                            helperText={errorMessage}
                            disabled={isSubmitting}
                          />
                        )
                      }
                    </Box>
                  )
                }}
              />
            )})}
            <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start" }}>
              <StyledButton label="Save &amp; Exit" type="outlined" onClick={handleSubmit(handleSaveAndExit)}></StyledButton>
              <StyledButton label="Back" type="outlined" disabled={isSubmitting || isLoading} onClick={handleClickBack}></StyledButton>
              <StyledButton label={"Next"} type="filled" onClick={handleSubmit(handleGoToNextForm)}></StyledButton>
            </Box>
            <SaveErrorAlert error={saveError} />
            <RevertToDraftDialog {...revertPrompt} />
            <DiscardChangesDialog
              open={showDiscardPrompt}
              onCancel={cancelDiscard}
              onDiscard={confirmDiscard}
            />
          </form>
        </Box>
      </div>

    </StyledContainer>
  );
}

const StyledContainer = styled(Container)({
  marginBottom: 24,
});

export default AddProjectStructuralSystem;